import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ExpenseSummary, ClientApprovalStatus } from '@peles/shared';
import * as portalApi from '../api/portal';
import type {
  ClientEvent,
  ClientEventDetail,
  ClientExpenseItem,
  ClientGuest,
  ClientFloorPlan,
  ClientPhoto,
  ClientTimeline,
  ClientChecklist,
  VendorEvent,
  VendorExpenseItem,
  VendorTimelineEntry,
  VendorProfile,
} from '../api/portal';

// ─── Client Portal ─────────────────────────────────────────────────────────

export function useClientEvents() {
  return useQuery<ClientEvent[]>({
    queryKey: ['portal', 'client', 'events'],
    queryFn: () => portalApi.getClientEvents(),
  });
}

export function useClientExpenseSummary(eventId: string) {
  return useQuery<ExpenseSummary>({
    queryKey: ['portal', 'client', 'expense-summary', eventId],
    queryFn: () => portalApi.getClientExpenseSummary(eventId),
    enabled: !!eventId,
  });
}

export function useClientExpenseItems(eventId: string) {
  return useQuery<ClientExpenseItem[]>({
    queryKey: ['portal', 'client', 'expenses', eventId],
    queryFn: () => portalApi.getClientExpenseItems(eventId),
    enabled: !!eventId,
  });
}

export function useClientEventDetail(eventId: string) {
  return useQuery<ClientEventDetail>({
    queryKey: ['portal', 'client', 'event-detail', eventId],
    queryFn: () => portalApi.getClientEventDetail(eventId),
    enabled: !!eventId,
  });
}

export function useClientEventGuests(eventId: string) {
  return useQuery<ClientGuest[]>({
    queryKey: ['portal', 'client', 'guests', eventId],
    queryFn: () => portalApi.getClientEventGuests(eventId),
    enabled: !!eventId,
  });
}

export function useClientEventFloorPlans(eventId: string) {
  return useQuery<ClientFloorPlan[]>({
    queryKey: ['portal', 'client', 'floor-plans', eventId],
    queryFn: () => portalApi.getClientEventFloorPlans(eventId),
    enabled: !!eventId,
  });
}

export function useClientEventPhotos(eventId: string) {
  return useQuery<ClientPhoto[]>({
    queryKey: ['portal', 'client', 'photos', eventId],
    queryFn: () => portalApi.getClientEventPhotos(eventId),
    enabled: !!eventId,
  });
}

export function useClientEventTimeline(eventId: string) {
  return useQuery<ClientTimeline[]>({
    queryKey: ['portal', 'client', 'timeline', eventId],
    queryFn: () => portalApi.getClientEventTimeline(eventId),
    enabled: !!eventId,
  });
}

export function useClientEventChecklist(eventId: string) {
  return useQuery<ClientChecklist[]>({
    queryKey: ['portal', 'client', 'checklist', eventId],
    queryFn: () => portalApi.getClientEventChecklist(eventId),
    enabled: !!eventId,
  });
}

export function useUpdateClientApproval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      expenseItemId,
      clientApproval,
    }: {
      expenseItemId: string;
      clientApproval: ClientApprovalStatus;
    }) => portalApi.updateClientApproval(expenseItemId, clientApproval),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'client', 'expenses'] });
    },
  });
}

// ─── Vendor Portal ─────────────────────────────────────────────────────────

export function useVendorEvents() {
  return useQuery<VendorEvent[]>({
    queryKey: ['portal', 'vendor', 'events'],
    queryFn: () => portalApi.getVendorEvents(),
  });
}

export function useVendorEventExpenses(eventId: string) {
  return useQuery<VendorExpenseItem[]>({
    queryKey: ['portal', 'vendor', 'expenses', eventId],
    queryFn: () => portalApi.getVendorEventExpenses(eventId),
    enabled: !!eventId,
  });
}

export function useVendorEventTimeline(eventId: string) {
  return useQuery<VendorTimelineEntry[]>({
    queryKey: ['portal', 'vendor', 'timeline', eventId],
    queryFn: () => portalApi.getVendorEventTimeline(eventId),
    enabled: !!eventId,
  });
}

export function useVendorProfile() {
  return useQuery<VendorProfile>({
    queryKey: ['portal', 'vendor', 'profile'],
    queryFn: () => portalApi.getVendorProfile(),
  });
}

export function useUpdateVendorProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      updates: Partial<Pick<VendorProfile, 'businessName' | 'phone' | 'email' | 'website'>>,
    ) => portalApi.updateVendorProfile(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'vendor', 'profile'] });
    },
  });
}

export function useUploadVendorDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      ...doc
    }: {
      eventId: string;
      fileName: string;
      documentType: string;
      expenseItemId?: string;
    }) => portalApi.uploadVendorDocument(eventId, doc),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'vendor'] });
    },
  });
}
